"use client";

import { useAccordion } from "@/components/useAccordion";
import { Checkbox } from "@cappelendamm/design-system/react";
import classNames from "classnames";
import { Minus, Plus } from "iconoir-react";
import { useItemListing } from "./ItemListingContext";
import { startTransition, useMemo, useOptimistic } from "react";
import {
  buildHierarchicalTree,
  type HierarchicalFacet,
} from "../utils/buildHierarchicalTree";
import { FacetHit } from "meilisearch";
import { useSearchParams } from "next/navigation";
import { FacetConfig } from "../constants";
import { useRefineFacet } from "../hooks/useRefineFacet";

export type HierarchicalMenuProps = {
  initialExpanded?: boolean;
  config: FacetConfig;
  sort?: (a: HierarchicalFacet, b: HierarchicalFacet) => number;
};

export function HierarchicalMenu({
  config: { attribute, title, queryParam = attribute },
  initialExpanded = true,
  sort,
}: HierarchicalMenuProps) {
  const { facets } = useItemListing();
  const searchParams = useSearchParams();

  const refine = useRefineFacet({ queryParam, isHierarchy: true });

  const items = useMemo(() => {
    // Collect all facets from the relevant attributes
    const selectedValues = searchParams.getAll(queryParam);
    const relevantFacets = Object.entries(facets).reduce<
      (FacetHit & { isRefined: boolean })[]
    >((acc, [key, facetsList]) => {
      if (!key.startsWith(attribute)) return acc;

      return [
        ...acc,
        ...facetsList.map((facet) => ({
          ...facet,
          isRefined: selectedValues.some((value) =>
            facet.value.endsWith(value),
          ),
        })),
      ];
    }, []);
    return buildHierarchicalTree(relevantFacets);
  }, [facets, attribute, searchParams]);

  const {
    isExpanded,
    aria: { button, panel },
    toggle,
  } = useAccordion(initialExpanded);

  if (items.length === 0) return null;

  return (
    <fieldset className="block h-fit">
      <div
        className={classNames(
          "grid grid-cols-[1fr_auto] items-center border-b",
          { "pb-4": isExpanded },
        )}
      >
        <legend onClick={toggle} className={"font-semibold"}>
          {title}
        </legend>
        <button
          className="flex h-12 w-12 items-center justify-end"
          onClick={toggle}
          {...button}
        >
          {isExpanded ? <Minus /> : <Plus />}
          <span className="sr-only">
            {isExpanded ? `Vis ${title}` : `Gjem ${title}`}
          </span>
        </button>
        <div
          className={classNames(
            isExpanded ? "grid" : "hidden",
            "col-span-full",
          )}
          {...panel}
        >
          {items.sort(sort).map((item) => (
            <HierarchicalItem
              item={item}
              key={item.value}
              attribute={attribute}
              refine={refine}
              sort={sort}
            />
          ))}
        </div>
      </div>
    </fieldset>
  );
}

function HierarchicalItem({
  item,
  className,
  attribute,
  refine,
  sort,
}: {
  item: HierarchicalFacet;
  className?: string;
  attribute: string;
  refine: (value: string) => void;
  sort?: (a: HierarchicalFacet, b: HierarchicalFacet) => number;
}) {
  const value = item.value.split("/").at(-1) ?? item.value;

  const [optimisticChecked, addOptimisticUpdate] = useOptimistic(
    item.isRefined,
  );

  return (
    <>
      <label
        className={classNames(
          "grid h-12 items-center gap-x-2",
          className ? className : "grid-cols-[auto_1fr_auto]",
        )}
      >
        <Checkbox
          checked={optimisticChecked}
          onChange={() => {
            startTransition(() => {
              addOptimisticUpdate(!item.isRefined);
              refine(item.value);
            });
          }}
        />
        <span className="truncate">{value}</span>
        <span className="text-right text-sm">{`${item.count}`}</span>
      </label>
      {optimisticChecked ? (
        <div className="col-span-full grid w-full grid-cols-[auto_1fr_auto] pl-4">
          {item.items?.sort(sort).map((child) => (
            <HierarchicalItem
              key={child.value}
              item={child}
              attribute={attribute}
              refine={refine}
              className="col-span-full grid-cols-subgrid"
              sort={sort}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}
